import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Box, ButtonBase, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { componentTokens } from "@accredo/fe-shared/theme/tokens/components.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";

import DocumentPreview from "./DocumentPreview.jsx";
import { metaForType } from "../constants/documentTypeMeta.js";

const SKELETON_ROWS = 3;

/**
 * The dashboard's "Aktualisierungen" card: the three documents most recently
 * imported among those the user may see - the same visibility as /dokumente,
 * since it asks the same endpoint. Documents imported together (the initial
 * import) fall back to their document date, newest first. A row opens the
 * document preview over the dashboard.
 *
 * Card chrome matches SparteSummaryCard.
 */
export default function RecentDocumentsCard() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [documents, setDocuments] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [previewDocument, setPreviewDocument] = useState(null);

  useEffect(() => {
    backendGet("/data/documents", {
      label: "Aktualisierungen",
      enqueueSnackbar,
      options: {
        params: {
          sort: "-imported_at,-document_date",
          limit: 3,
          include: "vertrag",
        },
      },
    }).then((response) => {
      if (Array.isArray(response?.data)) {
        setDocuments(response.data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    backendGet("/data/documents/type-counts", {
      label: "Dokumenttypen",
      enqueueSnackbar,
    }).then((response) => {
      setDocumentTypes(response?.document_types ?? []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = documents === null;

  return (
    <Box
      sx={{
        mb: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: theme.spacing(1),
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          backgroundColor: "card.headerBackground",
          px: { xs: 2, md: 4 },
          py: { xs: 1, md: 2.5 },
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
        >
          Aktualisierungen
        </Typography>
      </Box>

      {!isLoading && documents.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: { xs: 2, md: 4 }, py: 1.75 }}
        >
          Noch keine Dokumente vorhanden
        </Typography>
      )}

      <Stack>
        {isLoading
          ? Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <Box
                key={index}
                data-testid="recent-document-skeleton"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: { xs: 2, md: 4 },
                  py: 1.75,
                  borderTop:
                    index === 0
                      ? "none"
                      : `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Skeleton variant="rounded" width={36} height={36} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              </Box>
            ))
          : documents.map((doc, index) => {
              const { label, Icon: TypeIcon } = metaForType(
                doc.type,
                documentTypes,
              );
              const Icon = TypeIcon ?? DescriptionOutlined;
              const vertrags = doc.vertrags ?? [];
              const extra = vertrags.length - 1;

              return (
                <ButtonBase
                  key={doc.id}
                  data-testid={`recent-document-${doc.id}`}
                  onClick={() => setPreviewDocument(doc)}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: 2,
                    textAlign: "left",
                    width: "100%",
                    px: { xs: 2, md: 4 },
                    py: 1.75,
                    borderTop:
                      index === 0
                        ? "none"
                        : `1px solid ${theme.palette.grey[200]}`,
                    "&:hover": {
                      backgroundColor:
                        componentTokens.dataTable.row.hoverBackground,
                      // The icon square has the hover colour itself, so an
                      // outline keeps it visible.
                      "& .type-icon": {
                        outline: `1px solid ${theme.palette.grey[100]}`,
                      },
                    },
                  }}
                >
                  <Box
                    className="type-icon"
                    data-testid="recent-document-icon"
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: theme.spacing(1),
                      backgroundColor: theme.palette.grey[50],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon
                      fontSize="small"
                      sx={{ color: theme.palette.text.primary }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2">
                      <strong>{label}</strong>
                      {vertrags[0] && (
                        <span>
                          {" "}
                          {vertrags[0].vertragsnummer}
                          {extra > 0 ? ` +${extra}` : ""}
                        </span>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`Hinzugefügt am ${getDatum(doc.imported_at)}`}
                    </Typography>
                  </Box>
                </ButtonBase>
              );
            })}
      </Stack>

      <DocumentPreview
        open={Boolean(previewDocument)}
        document={previewDocument}
        onClose={() => setPreviewDocument(null)}
      />
    </Box>
  );
}
